import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints, formatApiError } from "../lib/api";
import { formatCurrency } from "../lib/romEngine";
import {
    ChartBar, TrendUp, TrendDown, Equals, Warning, Crown, ArrowsClockwise, Stack,
    X, ArrowRight, CaretRight,
} from "@phosphor-icons/react";

const DIR = {
    under: { label: "Under-estimated", color: "var(--rom-orange-dark)", Icon: TrendUp, hint: "Final invoices ran above ROM" },
    over: { label: "Over-estimated", color: "var(--rom-green)", Icon: TrendDown, hint: "Final invoices ran below ROM" },
    on: { label: "On target", color: "var(--rom-navy)", Icon: Equals, hint: "Within ±5% of ROM" },
    none: { label: "No data", color: "var(--rom-text-3)", Icon: Equals, hint: "" },
};

function pct(v) { return v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`; }

export default function Insights() {
    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8">
            <RepositoryInsightsContent />
        </div>
    );
}

export function RepositoryInsightsContent() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [drill, setDrill] = useState(null); // { title, subtitle, kind, items }
    const navigate = useNavigate();

    useEffect(() => { reload(); }, []);
    async function reload() {
        try {
            const { data } = await endpoints.repositoryInsights();
            setData(data); setErr("");
        } catch (e) { setErr(formatApiError(e)); }
    }

    if (!data) {
        return <div className="text-[var(--rom-text-2)]" data-testid="insights-loading">{err || "Loading repository insights…"}</div>;
    }

    const t = data.totals;
    const maxAbs = Math.max(1, ...data.by_loss_type.map((l) => Math.abs(l.avg_variance_pct || 0)));

    return (
        <div className="space-y-6" data-testid="insights-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-3">
                        <ChartBar size={24} weight="bold" className="text-[var(--rom-orange)]" />
                        ROM vs Actual · where the engine drifts
                    </h2>
                    <p className="text-sm text-[var(--rom-text-2)] mt-2 max-w-2xl leading-relaxed">
                        Every closed job across your repository, scored ROM-vs-final-invoice. Spot which loss types and
                        estimators <b className="text-[var(--rom-orange)]">systematically</b> over- or under-estimate so you can fix the lever, not the symptom.
                    </p>
                </div>
                <button onClick={reload} className="btn btn-ghost" data-testid="insights-refresh">
                    <ArrowsClockwise size={14} weight="bold" /> Refresh
                </button>
            </header>

            {err && <div className="p-3 rounded-[4px] text-sm border bg-red-50 border-red-200 text-red-700">{err}</div>}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Repository jobs" value={t.jobs} sub={`${t.imported} imported`} accent="navy" testId="ins-stat-jobs" />
                <Stat label="Closed with actuals" value={t.with_actuals} sub={`${t.tier_b} Tier-B breakdowns`} accent="orange" testId="ins-stat-closed" />
                <Stat label="Repository bias" value={pct(t.avg_variance_pct)} sub={t.avg_variance_pct > 0 ? "ROMs run low" : t.avg_variance_pct < 0 ? "ROMs run high" : "balanced"} accent={t.avg_variance_pct > 5 ? "orange" : "green"} testId="ins-stat-bias" />
                <Stat label="Mean abs error" value={t.mae_pct != null ? `±${t.mae_pct}%` : "—"} sub="avg |ROM − actual|" accent="navy" testId="ins-stat-mae" />
            </div>

            {/* Systematic callouts */}
            <div className="rom-card border-t-4 border-t-[var(--rom-orange)]">
                <div className="rom-card-head"><h3><Warning size={14} weight="bold" className="text-[var(--rom-orange)]" /> Systematic estimation gaps</h3></div>
                <div className="rom-card-body">
                    {data.systematic.length === 0 && (data.emerging || []).length === 0 ? (
                        <div className="text-sm text-[var(--rom-text-2)]" data-testid="insights-systematic-empty">
                            No systematic bias detected yet — no loss type is consistently off by ≥10% across 3+ closed jobs. Keep recording actuals to surface patterns.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {data.systematic.length > 0 && (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="insights-systematic">
                                    {data.systematic.map((s) => {
                                        const d = DIR[s.direction];
                                        return (
                                            <div key={s.loss_type} className="border border-[var(--rom-border)] rounded-[6px] p-4" style={{ borderLeftColor: d.color, borderLeftWidth: 4 }} data-testid={`systematic-${s.loss_type}`}>
                                                <div className="flex items-center justify-between">
                                                    <span className="font-semibold uppercase text-xs tracking-wider">{s.loss_type}</span>
                                                    <span className="chip" style={{ color: d.color, fontSize: "10px" }}><d.Icon size={11} weight="bold" /> {d.label}</span>
                                                </div>
                                                <div className="font-display font-black text-2xl mt-1" style={{ color: d.color }}>{pct(s.avg_variance_pct)}</div>
                                                <p className="text-[11px] text-[var(--rom-text-2)] mt-1 leading-snug">{s.note}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {(data.emerging || []).length > 0 && (
                                <div data-testid="insights-emerging">
                                    <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-2">Emerging signals · low sample</div>
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {data.emerging.map((s) => {
                                            const d = DIR[s.direction];
                                            return (
                                                <div key={s.loss_type} className="border border-dashed border-[var(--rom-border)] rounded-[6px] p-4 bg-amber-50/40" data-testid={`emerging-${s.loss_type}`}>
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-semibold uppercase text-xs tracking-wider">{s.loss_type}</span>
                                                        <span className="chip chip-gray" style={{ fontSize: "10px" }}>{s.samples} jobs</span>
                                                    </div>
                                                    <div className="font-display font-black text-2xl mt-1" style={{ color: d.color }}>{pct(s.avg_variance_pct)}</div>
                                                    <p className="text-[11px] text-[var(--rom-text-2)] mt-1 leading-snug">{s.note}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Biggest variance drivers — repository roll-up */}
            {data.variance_drivers && data.variance_drivers.tier_b_jobs > 0 && (() => {
                const vd = data.variance_drivers;
                const maxBucket = Math.max(1, ...vd.by_bucket.map((b) => Math.abs(b.delta_sum || 0)));
                return (
                    <div className="rom-card border-t-4 border-t-[var(--rom-navy)]" data-testid="insights-variance-drivers">
                        <div className="rom-card-head"><h3><ChartBar size={14} weight="bold" className="text-[var(--rom-navy)]" /> Biggest variance drivers · Schedule A–E roll-up</h3></div>
                        <div className="rom-card-body space-y-4">
                            {vd.headline && (
                                <div className="text-sm bg-[var(--rom-orange-soft)]/40 border border-[var(--rom-orange)]/30 rounded-[6px] p-3 leading-relaxed" data-testid="vd-headline">
                                    {vd.headline}
                                </div>
                            )}
                            <div className="text-[11px] text-[var(--rom-text-2)]">Net ROM-vs-actual dollar gap by schedule, across {vd.tier_b_jobs} closed Tier-B job{vd.tier_b_jobs !== 1 ? "s" : ""}.</div>
                            <div className="grid lg:grid-cols-2 gap-x-8 gap-y-3">
                                <div className="space-y-3">
                                    {vd.by_bucket.filter((b) => b.jobs > 0).map((b) => {
                                        const d = DIR[b.direction] || DIR.on;
                                        const w = Math.min(100, (Math.abs(b.delta_sum || 0) / maxBucket) * 100);
                                        return (
                                            <button key={b.key} data-testid={`vd-bucket-${b.key}`}
                                                onClick={() => setDrill({
                                                    title: b.label, kind: "bucket",
                                                    subtitle: `${b.jobs} job${b.jobs !== 1 ? "s" : ""} · net ${b.delta_sum > 0 ? "+" : ""}${formatCurrency(b.delta_sum)}`,
                                                    items: b.job_contributions || [],
                                                })}
                                                className="w-full text-left group">
                                                <div className="flex items-center justify-between text-xs mb-1">
                                                    <span className="font-semibold flex items-center gap-1">
                                                        {b.label}
                                                        <CaretRight size={11} weight="bold" className="text-[var(--rom-text-3)] opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </span>
                                                    <span className="font-mono font-bold flex items-center gap-1" style={{ color: d.color }}>
                                                        <d.Icon size={12} weight="bold" /> {b.delta_sum > 0 ? "+" : ""}{formatCurrency(b.delta_sum)}
                                                        {b.avg_pct != null && <span className="text-[10px] text-[var(--rom-text-3)] font-normal ml-1">{pct(b.avg_pct)} avg</span>}
                                                    </span>
                                                </div>
                                                <div className="h-2 rounded-full bg-slate-100 overflow-hidden group-hover:ring-1 group-hover:ring-[var(--rom-navy)]/30 transition-all">
                                                    <div className="h-full rounded-full" style={{ width: `${w}%`, background: d.color }} />
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-2">Top vendor / line drivers</div>
                                    {vd.top_vendors.length === 0 ? (
                                        <div className="text-xs text-[var(--rom-text-3)]">No per-vendor actuals captured yet.</div>
                                    ) : (
                                        <div className="space-y-1.5" data-testid="vd-vendors">
                                            {vd.top_vendors.map((v, i) => {
                                                const d = DIR[v.direction] || DIR.on;
                                                return (
                                                    <button key={i} data-testid={`vd-vendor-${i}`}
                                                        onClick={() => setDrill({
                                                            title: v.item + (v.vendor ? ` · ${v.vendor}` : ""), kind: "vendor",
                                                            subtitle: `${v.jobs} job${v.jobs !== 1 ? "s" : ""} · net ${v.delta > 0 ? "+" : ""}${formatCurrency(v.delta)}`,
                                                            items: v.job_contributions || [],
                                                        })}
                                                        className="w-full flex items-center justify-between gap-3 text-sm border border-[var(--rom-border)] rounded px-3 py-2 text-left hover:border-[var(--rom-navy)] hover:bg-slate-50/60 transition-colors group">
                                                        <div className="min-w-0">
                                                            <div className="font-medium truncate flex items-center gap-1">{v.item}{v.vendor ? ` · ${v.vendor}` : ""}
                                                                <CaretRight size={11} weight="bold" className="text-[var(--rom-text-3)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" /></div>
                                                            <div className="text-[10px] text-[var(--rom-text-3)]">quoted {formatCurrency(v.quoted_sum)} → actual {formatCurrency(v.actual_sum)} · {v.jobs} job{v.jobs !== 1 ? "s" : ""}</div>
                                                        </div>
                                                        <span className="font-mono text-sm font-semibold shrink-0" style={{ color: d.color }}>
                                                            {v.delta > 0 ? "+" : ""}{formatCurrency(v.delta)}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* By loss type */}
                <div className="rom-card lg:col-span-2">
                    <div className="rom-card-head"><h3><ChartBar size={14} weight="bold" /> Variance by loss type</h3></div>
                    {data.totals.with_actuals === 0 ? (
                        <div className="rom-card-body text-sm text-[var(--rom-text-3)]">No closed jobs yet.</div>
                    ) : (
                        <div className="rom-card-body space-y-3" data-testid="insights-by-loss">
                            {data.by_loss_type.filter((l) => l.samples > 0).map((l) => {
                                const d = DIR[l.direction];
                                const w = Math.min(100, (Math.abs(l.avg_variance_pct || 0) / maxAbs) * 100);
                                return (
                                    <div key={l.loss_type} data-testid={`ins-loss-${l.loss_type}`}>
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="font-semibold uppercase tracking-wider flex items-center gap-1.5">
                                                {l.loss_type}
                                                <span className="text-[var(--rom-text-3)] normal-case font-normal">{l.samples} jobs · {l.tier_b_samples} Tier-B</span>
                                            </span>
                                            <span className="font-mono font-bold flex items-center gap-1" style={{ color: d.color }}>
                                                <d.Icon size={12} weight="bold" /> {pct(l.avg_variance_pct)}
                                            </span>
                                        </div>
                                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                            <div className="h-full rounded-full" style={{ width: `${w}%`, background: d.color }} />
                                        </div>
                                        <div className="flex justify-between text-[10px] text-[var(--rom-text-3)] mt-1">
                                            <span>ROM {formatCurrency(l.rom_sum)} → actual {formatCurrency(l.actual_sum)}</span>
                                            <span>MAE ±{l.mae_pct ?? "—"}%</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* By source */}
                <div className="rom-card">
                    <div className="rom-card-head"><h3><Stack size={14} weight="bold" /> By ROM source</h3></div>
                    <div className="rom-card-body space-y-3" data-testid="insights-by-source">
                        {data.by_source.length === 0 ? (
                            <div className="text-sm text-[var(--rom-text-3)]">No data.</div>
                        ) : data.by_source.map((s) => {
                            const d = DIR[s.direction];
                            return (
                                <div key={s.source} className="border border-[var(--rom-border)] rounded-[4px] p-3" data-testid={`ins-source-${s.source}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">{s.source}</span>
                                        <span className="font-mono font-bold text-sm" style={{ color: d.color }}>{pct(s.avg_variance_pct)}</span>
                                    </div>
                                    <div className="text-[11px] text-[var(--rom-text-3)] mt-0.5">{s.samples} job{s.samples !== 1 ? "s" : ""} · MAE ±{s.mae_pct ?? "—"}%</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* By estimator */}
            <div className="rom-card">
                <div className="rom-card-head"><h3><Crown size={14} weight="bold" /> By estimator</h3></div>
                {data.by_estimator.length === 0 ? (
                    <div className="rom-card-body text-sm text-[var(--rom-text-3)]">No closed jobs yet.</div>
                ) : (
                    <table className="rom-table" data-testid="insights-by-estimator">
                        <thead>
                            <tr>
                                <th>Estimator</th>
                                <th className="numeric">Closed jobs</th>
                                <th className="numeric">Avg variance</th>
                                <th className="numeric">MAE</th>
                                <th>Tendency</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.by_estimator.map((u) => {
                                const d = DIR[u.direction];
                                return (
                                    <tr key={u.user_id} data-testid={`ins-est-${u.email}`}>
                                        <td>
                                            <div className="font-medium flex items-center gap-2">
                                                {u.name}
                                                {u.gold_standard && <Crown size={13} weight="fill" className="text-[var(--rom-orange)]" />}
                                            </div>
                                            <div className="text-[11px] text-[var(--rom-text-2)] font-mono">{u.email}</div>
                                        </td>
                                        <td className="numeric font-semibold">{u.samples}</td>
                                        <td className="numeric" style={{ color: d.color }}>{pct(u.avg_variance_pct)}</td>
                                        <td className="numeric font-semibold">{u.mae_pct != null ? `±${u.mae_pct}%` : "—"}</td>
                                        <td><span className="chip" style={{ color: d.color, fontSize: "10px" }}><d.Icon size={11} weight="bold" /> {d.label}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <VarianceDrillModal drill={drill} onClose={() => setDrill(null)} navigate={navigate} />
        </div>
    );
}

function VarianceDrillModal({ drill, onClose, navigate }) {
    if (!drill) return null;
    const items = drill.items || [];
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="vd-drill-modal">
            <div className="rom-card w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="rom-card-head flex items-center justify-between">
                    <div>
                        <h3 className="flex items-center gap-2"><ChartBar size={16} weight="bold" className="text-[var(--rom-navy)]" /> {drill.title}</h3>
                        <div className="text-[11px] text-[var(--rom-text-2)] mt-0.5">{drill.subtitle} · click a job to open it</div>
                    </div>
                    <button onClick={onClose} className="text-[var(--rom-text-3)] hover:text-[var(--rom-text)]" data-testid="vd-drill-close"><X size={18} /></button>
                </div>
                <div className="rom-card-body">
                    {items.length === 0 ? (
                        <div className="text-sm text-[var(--rom-text-3)]">No contributing jobs.</div>
                    ) : (
                        <table className="rom-table text-sm" data-testid="vd-drill-table">
                            <thead><tr>
                                <th>Job</th><th>Loss</th>
                                <th className="numeric">{drill.kind === "vendor" ? "Quoted" : "ROM"}</th>
                                <th className="numeric">Actual</th>
                                <th className="numeric">Δ</th>
                            </tr></thead>
                            <tbody>
                                {items.map((it, i) => {
                                    const over = it.delta > 0;
                                    return (
                                        <tr key={i} className="cursor-pointer" onClick={() => navigate(`/calculator/${it.job_id}`)} data-testid={`vd-drill-row-${i}`}>
                                            <td className="font-mono text-xs flex items-center gap-1">{it.ref || it.job_id} <ArrowRight size={11} weight="bold" className="text-[var(--rom-text-3)]" /></td>
                                            <td className="capitalize">{it.loss_type || "—"}</td>
                                            <td className="numeric">{formatCurrency(drill.kind === "vendor" ? it.quoted : it.predicted)}</td>
                                            <td className="numeric">{formatCurrency(it.actual)}</td>
                                            <td className="numeric font-bold" style={{ color: over ? "var(--rom-orange-dark)" : "var(--rom-green)" }}>
                                                {over ? "+" : ""}{formatCurrency(it.delta)}{it.pct != null ? ` (${it.pct > 0 ? "+" : ""}${it.pct}%)` : ""}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, sub, accent, testId }) {
    const color = { orange: "var(--rom-orange)", navy: "var(--rom-navy)", green: "var(--rom-green)" }[accent] || "var(--rom-navy)";
    return (
        <div className="rom-card" data-testid={testId}>
            <div className="rom-card-body">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">{label}</div>
                <div className="mt-2 font-display font-black text-3xl tabular tracking-tight" style={{ color }}>{value ?? "—"}</div>
                {sub && <div className="text-[11px] text-[var(--rom-text-3)] mt-1">{sub}</div>}
            </div>
        </div>
    );
}
